import { useRef, type CSSProperties } from 'react';
import { cn } from '../../lib/cn';
import { STag } from '../STag';
import {
  TAB_MAIN_SIZE,
  TAB_MAIN_COLORS,
  TAB_SUB_COLORS,
  TAB_TYPOGRAPHY,
  type STabSize,
  type STabOption,
} from './tabs.config';

export interface STabsProps {
  /** 현재 선택된 탭 value */
  value: string;
  /** 탭 옵션 목록 */
  tabs: STabOption[];
  /** 탭 변경 (sdUpdate) */
  onValueChange?: (value: string) => void;
  /** 탭 크기 (main 전용) */
  size?: STabSize;
  /** 서브 탭(밑줄형) 스타일 */
  isSub?: boolean;
  /** 세로 배치 (sub 전용 — main 폴더형은 항상 가로) */
  vertical?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * STabs — sd-tabs 포팅.
 * main = 폴더형(탭 줄에 파란 baseline, 탭은 위쪽만 둥글고 하단 테두리 없음),
 * sub = 밑줄형(선택 시 하단 라인). 뱃지(STag), 세로 배치, value 제어 + onValueChange(sdUpdate).
 */
export function STabs({
  value,
  tabs,
  onValueChange,
  size = 'md',
  isSub = false,
  vertical = false,
  className,
  style,
}: STabsProps) {
  const s = TAB_MAIN_SIZE[size];
  const blue = TAB_MAIN_COLORS.selected.border; // baseline 색 (토큰)
  // vertical 은 sub 전용. main 폴더형은 항상 가로.
  const isVertical = isSub && vertical;

  // roving tabindex + 방향키 내비게이션 (ARIA tabs 패턴, automatic activation).
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const moveTo = (target: number) => {
    if (target < 0 || target >= tabs.length) return;
    onValueChange?.(tabs[target].value);
    tabRefs.current[target]?.focus();
  };

  // 컨테이너: main 은 하단 baseline(파란선, 100% 너비), sub 는 없음
  const containerStyle: CSSProperties = { ...style };
  if (!isSub) containerStyle.borderBottom = `1px solid ${blue}`;

  return (
    <div
      role="tablist"
      aria-orientation={isVertical ? 'vertical' : 'horizontal'}
      className={cn(
        // main(가로): baseline(하단 divider)이 100% 너비로 채워지도록 w-full
        isSub ? 'inline-flex w-fit' : 'flex w-full',
        isVertical ? 'flex-col items-stretch' : 'flex-row',
        isSub ? (isVertical ? 'gap-[16px]' : 'gap-[24px]') : 'gap-[4px]',
        className,
      )}
      style={containerStyle}
    >
      {tabs.map((tab, i) => {
        const selected = tab.value === value;
        const select = () => onValueChange?.(tab.value);

        let tabStyle: CSSProperties;
        if (isSub) {
          tabStyle = {
            height: 20,
            paddingBottom: 4,
            fontSize: TAB_TYPOGRAPHY.fontSize,
            lineHeight: `${TAB_TYPOGRAPHY.lineHeight}px`,
            color: selected ? TAB_SUB_COLORS.selected.text : TAB_SUB_COLORS.default.text,
            borderBottom: `1px solid ${selected ? TAB_SUB_COLORS.selected.line : 'transparent'}`,
          };
        } else {
          const border = selected
            ? TAB_MAIN_COLORS.selected.border
            : TAB_MAIN_COLORS.default.border;
          const selectedBg = TAB_MAIN_COLORS.selected.bg; // baseline 을 덮을 흰색
          tabStyle = {
            height: s.height,
            padding: `0 ${s.paddingX}px`,
            fontSize: TAB_TYPOGRAPHY.fontSize,
            lineHeight: `${TAB_TYPOGRAPHY.lineHeight}px`,
            background: selected ? TAB_MAIN_COLORS.selected.bg : TAB_MAIN_COLORS.default.bg,
            color: selected ? TAB_MAIN_COLORS.selected.text : TAB_MAIN_COLORS.default.text,
            border: `1px solid ${border}`,
            // 폴더형: 미선택 탭은 baseline 위에, 선택 탭은 baseline(파란선)을 1px 덮어 연결된 것처럼 보이게
            borderTopLeftRadius: 6,
            borderTopRightRadius: 6,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderBottom: selected ? `1px solid ${selectedBg}` : 'none',
            marginBottom: selected ? -1 : 0,
          };
        }

        return (
          <div
            key={tab.value}
            ref={el => {
              tabRefs.current[i] = el;
            }}
            role="tab"
            // roving tabindex: 선택된 탭만 Tab 포커스 대상, 나머지는 방향키로 이동.
            tabIndex={selected ? 0 : -1}
            aria-selected={selected}
            aria-label={tab.label || 'tab'}
            onClick={select}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                select();
                return;
              }
              const nextKey = isVertical ? 'ArrowDown' : 'ArrowRight';
              const prevKey = isVertical ? 'ArrowUp' : 'ArrowLeft';
              if (e.key === nextKey) {
                e.preventDefault();
                moveTo((i + 1) % tabs.length);
              } else if (e.key === prevKey) {
                e.preventDefault();
                moveTo((i - 1 + tabs.length) % tabs.length);
              } else if (e.key === 'Home') {
                e.preventDefault();
                moveTo(0);
              } else if (e.key === 'End') {
                e.preventDefault();
                moveTo(tabs.length - 1);
              }
            }}
            className="relative box-border inline-flex cursor-pointer select-none items-center justify-center gap-[8px] whitespace-nowrap outline-none transition-[color,background-color] duration-200"
            style={tabStyle}
          >
            {/* 고스트(bold) 텍스트로 너비 고정 → 선택 시 두께 변화로 밀리지 않음 */}
            <span className="grid">
              <span aria-hidden className="invisible font-bold" style={{ gridArea: '1 / 1' }}>
                {tab.label}
              </span>
              <span style={{ gridArea: '1 / 1', fontWeight: selected ? 700 : 400 }}>
                {tab.label}
              </span>
            </span>
            {tab.badge !== undefined && tab.badge !== null && tab.badge !== '' && (
              <STag
                shape="square"
                size="xs"
                color={selected ? 'blue' : 'grey'}
                label={String(tab.badge)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
